/**
 * The mix.
 *
 * Three things can make noise at once — the commentary, the crowd bed and
 * whatever music is playing — and which of them should be loudest is not a
 * decision that can be made in advance. Between overs the music carries; the
 * moment a wicket falls the commentary has to cut through it. At a real ground
 * somebody rides those faders, so this gives the scorer the same three faders
 * rather than guessing on their behalf.
 *
 * Levels are remembered between sessions. A scorer who turned the music down
 * because their speaker is tinny should not have to do it again next Sunday.
 *
 * Kept deliberately separate from the on/off switches. Muting the crowd and
 * setting the crowd to zero look the same from the outside but are not the
 * same intent, and collapsing them means turning the crowd back on restores it
 * at a volume nobody chose.
 */

export type Channel = "commentary" | "crowd" | "music";

/**
 * Starting levels.
 *
 * Not all 1.0. These are the balance the app already shipped with, moved out of
 * three separate files and into one place — the crowd sat under the commentary
 * on purpose, and music under both.
 */
const DEFAULTS: Record<Channel, number> = {
  // Full. The boost above unity is now only +3 dB and the source carries the
  // level, so the top of the fader is the intended listening level rather than
  // a setting to be used sparingly.
  commentary: 1,
  crowd: 0.35,
  music: 0.6,
};

const KEY = "orbit.mix";

let levels: Record<Channel, number> = { ...DEFAULTS };
let listeners: ((m: Record<Channel, number>) => void)[] = [];

try {
  const raw = localStorage.getItem(KEY);
  if (raw) {
    const saved = JSON.parse(raw) as Partial<Record<Channel, number>>;
    for (const ch of Object.keys(DEFAULTS) as Channel[]) {
      const v = saved[ch];
      if (typeof v === "number" && v >= 0 && v <= 1) levels[ch] = v;
    }
  }
} catch { /* private mode, or someone edited it by hand */ }

export function get(ch: Channel): number {
  return levels[ch];
}

export function all(): Record<Channel, number> {
  return { ...levels };
}

/**
 * Set a level.
 *
 * Clamped rather than trusted: this comes from a range input, and a slider that
 * reports 1.02 would push a gain node above unity and clip.
 */
export function set(ch: Channel, value: number): void {
  const v = Math.min(1, Math.max(0, Number.isFinite(value) ? value : DEFAULTS[ch]));
  if (levels[ch] === v) return;
  levels[ch] = v;
  try { localStorage.setItem(KEY, JSON.stringify(levels)); } catch { /* private mode */ }
  for (const fn of listeners) fn({ ...levels });
}

export function reset(): void {
  levels = { ...DEFAULTS };
  try { localStorage.setItem(KEY, JSON.stringify(levels)); } catch { /* private mode */ }
  for (const fn of listeners) fn({ ...levels });
}

/** Subscribe. Fires immediately so a control paints at the right position. */
export function onChange(fn: (m: Record<Channel, number>) => void): () => void {
  listeners.push(fn);
  fn({ ...levels });
  return () => { listeners = listeners.filter((f) => f !== fn); };
}
